import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Share2, Download, Copy, Loader2,
  MessageCircle, Twitter, Facebook, Link2, Mail, Wallet, Palette,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { generateBudgetCard, CARD_THEMES } from './ShareCardRenderer';

const ThemeSelector = ({ activeTheme, onSelect }) => (
  <div className="flex items-center gap-2" data-testid="budget-theme-selector">
    <Palette className="w-4 h-4 text-ocean/50 flex-shrink-0" />
    <div className="flex gap-2">
      {Object.values(CARD_THEMES).map((t) => (
        <button
          key={t.id}
          data-testid={`budget-theme-${t.id}`}
          onClick={() => onSelect(t.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border-2 ${
            activeTheme === t.id
              ? 'border-ocean shadow-md scale-105'
              : 'border-transparent hover:border-ocean/20 opacity-70 hover:opacity-100'
          }`}
          style={{ background: t.swatch[0], color: t.swatch[1] }}
        >
          <span className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ background: t.swatch[1] }} />
          {t.name}
        </button>
      ))}
    </div>
  </div>
);

export const BudgetSocialShareDialog = ({
  open,
  onOpenChange,
  itineraryId,
  tripInput,
  generatedItinerary,
  currencyMode,
  convertedBudget,
  onEmailShare,
}) => {
  const [shareImage, setShareImage] = useState(null);
  const [shareText, setShareText] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTheme, setActiveTheme] = useState('ocean');
  const [photoIndex, setPhotoIndex] = useState(0);
  const photosRef = useRef(null);

  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join(', ')
    : tripInput?.destination_city || 'Trip';
  const country = tripInput?.destination_country || '';

  const displayItinerary = (() => {
    if (currencyMode === 'home' && convertedBudget && generatedItinerary) {
      return { ...generatedItinerary, estimated_total_budget: convertedBudget };
    }
    return generatedItinerary;
  })();

  const budgetObj = displayItinerary?.estimated_total_budget;
  const totalText = typeof budgetObj === 'object' ? budgetObj?.total : String(budgetObj || '');
  const fallbackText = `Check out my trip budget for ${city}, ${country}! Total: ${totalText}. Plan yours at tripleabooking.com`;

  const fetchPhotos = useCallback(async () => {
    if (photosRef.current !== null) return photosRef.current;
    const API = process.env.REACT_APP_BACKEND_URL;
    photosRef.current = [];
    try {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 12000);
      const searchCity = tripInput?.destination_cities?.[0] || tripInput?.destination_city || city;
      const resp = await fetch(`${API}/api/destination-photos?city=${encodeURIComponent(searchCity)}&country=${encodeURIComponent(country)}&count=3`, { signal: ctrl.signal });
      clearTimeout(tid);
      const data = await resp.json();
      if (data.success && data.images?.length) {
        photosRef.current = data.images;
      }
    } catch { /* silent */ }
    return photosRef.current;
  }, [tripInput, city, country]);

  const buildCard = useCallback(async (themeId, pIdx) => {
    try {
      const photos = await fetchPhotos();
      const photoUrl = photos[pIdx] || photos[0] || null;
      const dataUrl = await generateBudgetCard(tripInput, displayItinerary, photoUrl, themeId);
      if (dataUrl) {
        setShareImage(dataUrl);
        setShareText(fallbackText);
      } else {
        toast.error('Failed to generate card.');
        setShareText(fallbackText);
      }
    } catch (err) {
      toast.error('Card generation failed.');
      setShareText(fallbackText);
    } finally {
      setLoading(false);
    }
  }, [tripInput, displayItinerary, fallbackText, fetchPhotos]);

  const openRef = useRef(open);
  useEffect(() => {
    const wasOpen = openRef.current;
    openRef.current = open;
    if (open && !wasOpen) {
      setLoading(true);
      buildCard('ocean', 0);
    }
    if (!open && wasOpen) {
      setShareImage(null);
      setShareText('');
      setLoading(false);
      setActiveTheme('ocean');
      setPhotoIndex(0);
    }
  }, [open, buildCard]);

  const handleThemeChange = async (themeId) => {
    setActiveTheme(themeId);
    setLoading(true);
    await buildCard(themeId, photoIndex);
  };

  const handlePhotoPrev = async () => {
    const photos = photosRef.current || [];
    if (photos.length <= 1) return;
    const newIdx = (photoIndex - 1 + photos.length) % photos.length;
    setPhotoIndex(newIdx);
    setLoading(true);
    await buildCard(activeTheme, newIdx);
  };

  const handlePhotoNext = async () => {
    const photos = photosRef.current || [];
    if (photos.length <= 1) return;
    const newIdx = (photoIndex + 1) % photos.length;
    setPhotoIndex(newIdx);
    setLoading(true);
    await buildCard(activeTheme, newIdx);
  };

  const photoCount = photosRef.current?.length || 0;

  const downloadImage = () => {
    if (!shareImage) return;
    const link = document.createElement('a');
    link.href = shareImage;
    link.download = `${city.replace(/\s+/g, '-')}-budget-card.png`;
    link.click();
    toast.success('Budget card downloaded!');
  };

  const copyText = async () => {
    try { await navigator.clipboard.writeText(shareText); toast.success('Caption copied!'); }
    catch { toast.error('Failed to copy'); }
  };

  const shareNative = async () => {
    if (!navigator.share) { toast.error('Native sharing not supported'); return; }
    try {
      const shareData = { title: `${city} Trip Budget`, text: shareText };
      if (shareImage) {
        try {
          const resp = await fetch(shareImage);
          const blob = await resp.blob();
          const file = new File([blob], 'budget-card.png', { type: 'image/png' });
          if (navigator.canShare?.({ files: [file] })) shareData.files = [file];
        } catch {}
      }
      await navigator.share(shareData);
    } catch (err) { if (err.name !== 'AbortError') toast.error('Sharing failed'); }
  };

  const shareWhatsApp = () => window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareTwitter = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareText)}`, '_blank');
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }
    catch { toast.error('Failed to copy link'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white/95 backdrop-blur-xl max-h-[90vh] overflow-y-auto" data-testid="budget-social-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-ocean flex items-center gap-2">
            <Wallet className="w-5 h-5 text-gold" />
            Share Trip Budget
          </DialogTitle>
          <DialogDescription>Share your {city} trip budget on social media or via email</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Theme selector */}
          <ThemeSelector activeTheme={activeTheme} onSelect={handleThemeChange} />

          {/* Budget Card Image with photo carousel arrows */}
          <div className="relative rounded-xl overflow-hidden border border-ocean/10 shadow-md bg-ocean/5 group" data-testid="budget-share-card-container">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-ocean mb-3" />
                <p className="text-ocean/60 text-sm">Creating budget card...</p>
              </div>
            ) : shareImage ? (
              <>
                <img src={shareImage} alt={`Budget for ${city}`} className="w-full h-auto" data-testid="budget-share-card-image" />
                {/* Photo navigation arrows */}
                {photoCount > 1 && (
                  <>
                    <button
                      onClick={handlePhotoPrev}
                      data-testid="budget-photo-prev-btn"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-sm"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handlePhotoNext}
                      data-testid="budget-photo-next-btn"
                      className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 backdrop-blur-sm"
                      style={{ left: 'calc(50% - 44px)' }}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    {/* Dot indicators */}
                    <div className="absolute bottom-3 flex gap-1.5 justify-center" style={{ left: '0', width: '50%' }} data-testid="budget-photo-dots">
                      {Array.from({ length: photoCount }).map((_, i) => (
                        <span
                          key={`photo-option-${i}`}
                          className={`w-2 h-2 rounded-full transition-all duration-200 ${
                            i === photoIndex ? 'bg-white scale-125 shadow-md' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-16">
                <p className="text-ocean/40">Failed to generate card</p>
              </div>
            )}
          </div>

          {/* Caption */}
          {shareText && (
            <div className="bg-ocean/5 rounded-xl p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-charcoal flex-1">{shareText}</p>
                <Button variant="ghost" size="icon" onClick={copyText} className="h-7 w-7 flex-shrink-0 text-ocean/50 hover:text-ocean" data-testid="budget-copy-caption-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Social Share Buttons */}
          <div className="grid grid-cols-2 gap-2">
            {navigator.share && (
              <Button onClick={shareNative} className="bg-ocean hover:bg-ocean-light text-white rounded-xl h-11 col-span-2" data-testid="budget-native-share-btn">
                <Share2 className="w-4 h-4 mr-2" /> Share
              </Button>
            )}
            <Button onClick={shareWhatsApp} variant="outline" className="rounded-xl h-11 border-green-300 text-green-700 hover:bg-green-50" data-testid="budget-whatsapp-share-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button onClick={shareTwitter} variant="outline" className="rounded-xl h-11 border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="budget-twitter-share-btn">
              <Twitter className="w-4 h-4 mr-2" /> Twitter / X
            </Button>
            <Button onClick={shareFacebook} variant="outline" className="rounded-xl h-11 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="budget-facebook-share-btn">
              <Facebook className="w-4 h-4 mr-2" /> Facebook
            </Button>
            <Button onClick={copyLink} variant="outline" className="rounded-xl h-11 border-ocean/20 text-ocean hover:bg-ocean/5" data-testid="budget-copy-link-btn">
              <Link2 className="w-4 h-4 mr-2" /> Copy Link
            </Button>
            <Button onClick={() => { onOpenChange(false); onEmailShare?.(); }} variant="outline" className="rounded-xl h-11 border-amber-300 text-amber-700 hover:bg-amber-50 col-span-2" data-testid="budget-email-share-btn">
              <Mail className="w-4 h-4 mr-2" /> Email to Friends
            </Button>
          </div>

          {/* Download */}
          {shareImage && (
            <Button onClick={downloadImage} variant="outline" className="w-full rounded-xl h-11 border-gold/30 text-gold-dark hover:bg-gold/10" data-testid="budget-download-card-btn">
              <Download className="w-4 h-4 mr-2" /> Download Budget Card
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
